/**
 * Animate a series of words with a rolling text/cube flip effect
 * Creates a cube (rectangular prism) with front and bottom faces
 */
class RollText {
  constructor(el) {
    this.el = el;
    this.words = this.parseWords();
    this.currentIndex = 0;
    this.animationStarted = false;
    
    if (this.words.length <= 1) return;
    
    this.init();
    this.setupIntersectionObserver();
  }
  
  parseWords() {
    const text = this.el.getAttribute('data-text');
    if (!text) return [];

    // console.log(text)
    
    // Split by comma and trim whitespace
    return text.split(',').map(word => word.trim()).filter(word => word.length > 0);
  }
  
  init() {
    const cube = this.el.querySelector('.roll-text-cube');
    
    // Create front face (current word)
    const frontFace = document.createElement('span');
    frontFace.className = 'roll-text-face roll-text-face-front';
    frontFace.textContent = this.words[0];
    
    // Create bottom face (next word)
    const bottomFace = document.createElement('span');
    bottomFace.className = 'roll-text-face roll-text-face-bottom';
    bottomFace.textContent = this.words[1] || this.words[0];
    
    cube.textContent = "";
    cube.appendChild(frontFace);
    cube.appendChild(bottomFace);
    
    this.cube = cube;
    this.frontFace = frontFace;
    this.bottomFace = bottomFace;
    
    // Calculate and set initial dimensions
    this.updateDimensions();
    
    // Set up resize listener
    this.setupResizeListener();
  }
  
  updateDimensions() {
    // Get computed styles from the element to match surrounding text
    const computedStyle = window.getComputedStyle(this.el);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    
    // Create a temporary element to measure all words
    const tempEl = document.createElement('span');
    tempEl.style.visibility = 'hidden';
    tempEl.style.position = 'absolute';
    tempEl.style.whiteSpace = 'nowrap';
    tempEl.style.fontSize = computedStyle.fontSize;
    tempEl.style.fontFamily = computedStyle.fontFamily;
    tempEl.style.fontWeight = computedStyle.fontWeight;
    tempEl.style.fontStyle = computedStyle.fontStyle;
    tempEl.style.letterSpacing = computedStyle.letterSpacing;
    document.body.appendChild(tempEl);
    
    // Get width of longest word
    let maxWidth = 0;
    this.words.forEach(word => {
      tempEl.textContent = word;
      const rect = tempEl.getBoundingClientRect();
      maxWidth = Math.max(maxWidth, rect.width) + 15; //5
      // console.log(`Word: "${word}", Width: ${rect.width}, MaxWidth: ${maxWidth}`);
    });
    
    // Remove temporary element
    document.body.removeChild(tempEl);
    
    // Set depth for translateZ (half of height)
    const depth = lineHeight / 2;
    this.depth = depth;
    
    // Set cube and wrapper dimensions based on longest word and line height
    this.cube.style.width = `${maxWidth}px`;
    this.cube.style.height = `${lineHeight}px`;
    this.cube.style.transform = `translateZ(-${depth}px)`;
    this.el.style.width = `${maxWidth}px`;
    this.el.style.height = `${lineHeight}px`;
    
    // Update face transforms with new depth
    this.frontFace.style.transform = `rotateX(0deg) translateZ(${depth}px)`;
    this.bottomFace.style.transform = `rotateX(-90deg) translateZ(${depth}px)`;
  }
  
  setupResizeListener() {
    // Debounce resize handler to avoid excessive calculations
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateDimensions();
      }, 150); // Wait 150ms after resize stops
    };
    
    window.addEventListener('resize', handleResize);
    
    // Store cleanup function for potential future use
    this.removeResizeListener = () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }
  
  // Start animation when element is scrolled into view
  setupIntersectionObserver() {
    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: start animation immediately if Intersection Observer is not supported
      this.startAnimation();
      return;
    }
    
    // Create observer to watch when element comes into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Start animation when element is visible
        if (entry.isIntersecting && !this.animationStarted) {
          this.animationStarted = true;
          this.startAnimation();
          // Disconnect observer after starting animation
          observer.disconnect();
        }
      });
    }, {
      // Start animation when element is 50% visible
      threshold: 0.5
    });
    
    // Start observing the element
    observer.observe(this.el);
  }
  
  startAnimation() {
    // Start animation after brief delay
    setTimeout(() => {
      this.animateToNext();
    }, 1500);
  }
  
  animateToNext() {
    if (this.words.length <= 1) return;

    // Get next word
    this.currentIndex = (this.currentIndex + 1) % this.words.length;
    const nextWord = this.words[this.currentIndex];
    
    // Update bottom face with next word before flipping
    this.bottomFace.textContent = nextWord;
    
    // Add flipping class to animate face transforms
    this.cube.classList.add('flipping');
    
    // Update transforms for animation
    this.frontFace.style.transform = `rotateX(90deg) translateZ(${this.depth}px)`;
    this.bottomFace.style.transform = `rotateX(0deg) translateZ(${this.depth}px)`;
    
    // After animation completes, swap face positions/transforms and reset
    setTimeout(() => {
      // Remove flipping class first to reset transforms
      this.cube.classList.remove('flipping');
      
      // Front face becomes new (next) bottom (rotateX(-90deg)) - hide it first
      this.frontFace.classList.remove('roll-text-face-front');
      this.frontFace.classList.add('roll-text-face-bottom');
      this.frontFace.style.transform = `rotateX(-90deg) translateZ(${this.depth}px)`;
      this.frontFace.setAttribute("aria-hidden","true");
      
      // Bottom face becomes new (current) front (rotateX(0deg))
      this.bottomFace.classList.remove('roll-text-face-bottom');
      this.bottomFace.classList.add('roll-text-face-front');
      this.bottomFace.style.transform = `rotateX(0deg) translateZ(${this.depth}px)`;
      this.bottomFace.removeAttribute("aria-hidden");
      
      // Swap the face references
      const tempFace = this.frontFace;
      this.frontFace = this.bottomFace;
      this.bottomFace = tempFace;
      
      // Update new (next) bottom face with the word after next
      const nextNextIndex = (this.currentIndex + 1) % this.words.length;
      this.bottomFace.textContent = this.words[nextNextIndex];
      
      // Schedule next animation
      setTimeout(() => {
        this.animateToNext();
      }, 1000);
    }, 300); // Wait for transition to complete
  }
}

// Initialize all roll-text elements
function initRollText() {
  const elements = document.querySelectorAll('.animate-roll-text');
  
  elements.forEach(el => {
    new RollText(el);
  });
}

// Auto-init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRollText);
} else {
  initRollText();
}

export default RollText;
